/**
 * Gen1 state management capabilities (on/off/toggle)
 * Used by Gen1 relays, dimmers, and lights
 *
 * Gen1 uses HTTP endpoints: /device/relay/control, /device/light/control
 * Instead of Gen2 RPC methods
 */

/**
 * Gen1 turn action for switching devices on/off
 * @param {string} controlType - Control type ('relay', 'light', 'roller')
 * @param {boolean} supportsPower - Whether device has power monitoring
 */
export function createGen1TurnAction(controlType = 'relay', supportsPower = false) {
	return {
		name: 'Turn On/Off',
		description: 'Turn the device on, off, or toggle its current state',
		parameters: {
			state: {
				type: 'enum',
				required: true,
				values: ['on', 'off', 'toggle'],
				default: 'on',
				description: 'Desired state',
			},
			timer: {
				type: 'number',
				required: false,
				min: 1,
				max: 7200,
				unit: 'seconds',
				description: 'Auto-off timer duration',
			},
		},
		executeAction: async (device, channel, params) => {
			const { state, timer } = params;
			const channelId = parseInt(channel.split(':')[1]) || 0;

			const payload = {
				id: device.id,
				channel: channelId,
				turn: state, // Gen1 uses 'turn' parameter with 'on', 'off', 'toggle'
			};

			if (timer) {
				payload.timer = timer;
			}

			return {
				endpoint: `/device/${controlType}/control`,
				method: 'POST',
				params: payload,
			};
		},
	};
}

/**
 * Gen1 state condition (is device on/off)
 * Gen1 devices use relays[channel].ison path instead of switch:0.output
 */
export function createGen1StateCondition(controlType = 'relay') {
	return {
		name: 'Device State',
		description: 'Check if the device is currently on or off',
		parameters: {
			state: {
				type: 'boolean',
				required: true,
				description: 'Expected state (true = on, false = off)',
			},
		},
		evaluateCondition: (device, channel, params) => {
			const channelId = parseInt(channel.split(':')[1]) || 0;
			const statePath = controlType === 'light' ? 'lights' : 'relays';
			const currentState = device.status?.[statePath]?.[channelId]?.ison;
			return currentState === params.state;
		},
	};
}

/**
 * Gen1 state change trigger
 */
export function createGen1StateChangedTrigger(controlType = 'relay') {
	return {
		name: 'State Changed',
		description: 'Fires when the device state changes',
		parameters: {
			fromState: {
				type: 'enum',
				required: false,
				values: ['on', 'off', 'any'],
				default: 'any',
				description: 'Previous state filter',
			},
			toState: {
				type: 'enum',
				required: false,
				values: ['on', 'off', 'any'],
				default: 'any',
				description: 'New state filter',
			},
		},
		evaluateTrigger: (device, channel, previousStatus, currentStatus, params) => {
			const channelId = parseInt(channel.split(':')[1]) || 0;
			const statePath = controlType === 'light' ? 'lights' : 'relays';

			const prevState = previousStatus?.[statePath]?.[channelId]?.ison;
			const currState = currentStatus?.[statePath]?.[channelId]?.ison;

			if (prevState === currState) {
				return false;
			}

			const { fromState, toState } = params;
			const prevMatch =
				fromState === 'any' ||
				(fromState === 'on' && prevState) ||
				(fromState === 'off' && !prevState);
			const currMatch =
				toState === 'any' ||
				(toState === 'on' && currState) ||
				(toState === 'off' && !currState);

			return prevMatch && currMatch;
		},
	};
}
